<template>
  <div class="bg-white rounded-xl shadow-sm p-6 sticky top-6">
    <!-- Search Input -->
    <div class="mb-6">
      <label class="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
      <input
        :value="filters.searchQuery"
        @input="updateFilter('searchQuery', $event.target.value)"
        type="text"
        placeholder="Zoek naar product..."
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-transparent"
      />
    </div>

    <!-- Filter Categories (Fixed, Non-scrolling) -->
    <div class="space-y-3">
      <!-- URNE Section -->
      <div v-if="urneCategories.length > 0">
        <h3 class="text-sm font-bold text-gray-900 mb-2">URNE:</h3>
        <div class="space-y-2 pl-4">
          <label v-for="item in urneCategories" :key="item.value" class="flex items-center cursor-pointer group">
            <input
              :checked="filters.selectedCategories.includes(item.value)"
              @change="toggleArrayFilter('selectedCategories', item.value)"
              type="checkbox"
              class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
            />
            <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ item.label }}</span>
          </label>
        </div>
      </div>

      <!-- FOTOKADER Section -->
      <div v-if="fotokaderCategories.length > 0">
        <h3 class="text-sm font-bold text-gray-900 mb-2">FOTOKADER:</h3>
        <div class="space-y-2 pl-4">
          <label v-for="item in fotokaderCategories" :key="item.value" class="flex items-center cursor-pointer group">
            <input
              :checked="filters.selectedCategories.includes(item.value)"
              @change="toggleArrayFilter('selectedCategories', item.value)"
              type="checkbox"
              class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
            />
            <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ item.label }}</span>
          </label>
        </div>
      </div>

      <!-- SIERRADEN Section -->
      <div v-if="sierradenCategories.length > 0">
        <h3 class="text-sm font-bold text-gray-900 mb-2">SIERRADEN:</h3>
        <div class="space-y-2 pl-4">
          <label v-for="item in sierradenCategories" :key="item.value" class="flex items-center cursor-pointer group">
            <input
              :checked="filters.selectedCategories.includes(item.value)"
              @change="toggleArrayFilter('selectedCategories', item.value)"
              type="checkbox"
              class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
            />
            <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ item.label }}</span>
          </label>
        </div>
      </div>

      <!-- ANDERE Section -->
      <div v-if="andereCategories.length > 0">
        <h3 class="text-sm font-bold text-gray-900 mb-2">ANDERE:</h3>
        <div class="space-y-2 pl-4">
          <label v-for="item in andereCategories" :key="item.value" class="flex items-center cursor-pointer group">
            <input
              :checked="filters.selectedCategories.includes(item.value)"
              @change="toggleArrayFilter('selectedCategories', item.value)"
              type="checkbox"
              class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
            />
            <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ item.label }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Active Filters Display -->
    <div v-if="activeFiltersCount > 0" class="mb-6 p-4 bg-theme-light rounded-lg border border-theme-border">
      <h4 class="text-sm font-semibold text-theme mb-3">Actieve filters ({{ activeFiltersCount }})</h4>
      <div class="flex flex-wrap gap-2">
        <span v-if="filters.searchQuery" class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full">
          "{{ filters.searchQuery }}"
          <button @click="updateFilter('searchQuery', '')" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span
          v-for="category in filters.selectedCategories"
          :key="category"
          class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full"
        >
          {{ getCategoryLabel(category) }}
          <button @click="toggleArrayFilter('selectedCategories', category)" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
      </div>
    </div>

    <!-- Clear Filters Button -->
    <button
      @click="clearAllFilters"
      class="w-full px-6 py-3 text-sm font-semibold text-theme bg-theme-light border border-theme-border rounded-lg hover:bg-theme-bg transition-colors"
      :disabled="activeFiltersCount === 0"
      :class="{ 'opacity-50 cursor-not-allowed': activeFiltersCount === 0 }"
    >
      Alle filters wissen
    </button>
  </div>
</template>

<script>
export default {
  name: 'FilterSidebar',
  props: {
    filters: {
      type: Object,
      required: true
    },
    vacations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // New category structure per client requirements
      categoryStructure: {
        urne: [
          'pootafdruk',
          'hart',
          'kaars',
          'veelzijdig',
          'hond (beeltenis)',
          'kat (beeltenis)',
          'konijn (beeltenis)',
          'basis',
          'mini (reisurne)',
          'foto',
          'ecologisch',
          'handgemaakt',
          'strooiurne'
        ],
        fotokader: [
          'met asruimte',
          'zonder asruimte'
        ],
        sierraden: [
          'sleutelhanger',
          'ring',
          'armband',
          'ketting',
          'divers'
        ],
        andere: [
          'gips',
          'stempel',
          'memorybox',
          'schilderij',
          'fotoboek',
          'troostknuffel',
          'andere'
        ]
      },
      
      // Labels for better display
      labelMappings: {
        // URNE items
        'pootafdruk': 'Pootafdruk',
        'hart': 'Hart',
        'kaars': 'Kaars',
        'veelzijdig': 'Veelzijdig',
        'hond (beeltenis)': 'Hond (beeltenis)',
        'kat (beeltenis)': 'Kat (beeltenis)',
        'konijn (beeltenis)': 'Konijn (beeltenis)',
        'basis': 'Basis',
        'mini (reisurne)': 'Mini (reisurne)',
        'foto': 'Foto',
        'ecologisch': 'Ecologisch',
        'handgemaakt': 'Handgemaakt',
        'strooiurne': 'Strooiurne',
        
        // FOTOKADER items
        'met asruimte': 'met asruimte',
        'zonder asruimte': 'zonder asruimte',
        
        // SIERRADEN items
        'sleutelhanger': 'Sleutelhanger',
        'ring': 'Ring',
        'armband': 'Armband',
        'ketting': 'Ketting',
        'divers': 'Divers',
        
        // ANDERE items
        'gips': 'Gips',
        'stempel': 'Stempel',
        'memorybox': 'Memorybox',
        'schilderij': 'Schilderij',
        'fotoboek': 'Fotoboek',
        'troostknuffel': 'Troostknuffel',
        'andere': 'Andere',
        
        // Backward compatibility for old categories
        'hond': 'Hond (beeltenis)',
        'kat': 'Kat (beeltenis)',
        'konijn': 'Konijn (beeltenis)',
        'fotokader': 'Fotokader'
      },
      
      // Categories to exclude from filtering
      excludeFromFilters: ['producten', 'assortiment', 'weg']
    }
  },
  
  computed: {
    // Extract all unique categories from vacation data
    allCategories() {
      const categories = new Set()
      const hideLabels = this.$root.shopSettings?.hideLabels || []
      
      this.vacations.forEach(vacation => {
        if (vacation.metadata && vacation.metadata.categories) {
          vacation.metadata.categories.forEach(category => {
            const lowerCategory = category.toLowerCase()
            // Don't include categories that should be hidden from display
            if (!hideLabels.includes(lowerCategory) && 
                !this.excludeFromFilters.includes(lowerCategory)) {
              categories.add(lowerCategory)
            }
          })
        }
      })
      return Array.from(categories)
    },

    // URNE categories
    urneCategories() {
      return this.categoryStructure.urne
        .filter(cat => {
          // Map beeltenis categories back to original names for checking
          const checkCat = cat.replace(' (beeltenis)', '')
          return this.allCategories.includes(cat) || 
                 this.allCategories.includes(checkCat) ||
                 this.allCategories.some(c => c.includes(checkCat))
        })
        .map(cat => ({
          value: cat,
          label: this.labelMappings[cat] || this.formatLabel(cat)
        }))
    },

    // FOTOKADER categories
    fotokaderCategories() {
      // Only show fotokader subcategories if they actually exist in the data
      // Otherwise, show fotokader as a simple category
      const hasMetAsruimte = this.allCategories.includes('met asruimte')
      const hasZonderAsruimte = this.allCategories.includes('zonder asruimte')
      const hasFotokader = this.allCategories.includes('fotokader')
      
      // If the specific subcategories exist, show them
      if (hasMetAsruimte || hasZonderAsruimte) {
        return this.categoryStructure.fotokader
          .filter(cat => this.allCategories.includes(cat))
          .map(cat => ({
            value: cat,
            label: this.labelMappings[cat] || this.formatLabel(cat)
          }))
      }
      // Otherwise, if fotokader exists, show it as a single option
      else if (hasFotokader) {
        return [{
          value: 'fotokader',
          label: 'Fotokader'
        }]
      }
      
      return []
    },

    // SIERRADEN categories
    sierradenCategories() {
      return this.categoryStructure.sierraden
        .filter(cat => 
          this.allCategories.includes(cat) ||
          this.allCategories.some(c => c.includes(cat))
        )
        .map(cat => ({
          value: cat,
          label: this.labelMappings[cat] || this.formatLabel(cat)
        }))
    },

    // ANDERE categories
    andereCategories() {
      return this.categoryStructure.andere
        .filter(cat => 
          this.allCategories.includes(cat) ||
          this.allCategories.some(c => c.includes(cat))
        )
        .map(cat => ({
          value: cat,
          label: this.labelMappings[cat] || this.formatLabel(cat)
        }))
    },
    
    activeFiltersCount() {
      let count = 0
      if (this.filters.searchQuery) count++
      count += this.filters.selectedCategories?.length || 0
      return count
    }
  },
  
  methods: {
    updateFilter(filterName, value) {
      this.$emit('filter-change', { [filterName]: value })
    },
    
    toggleArrayFilter(filterName, value) {
      const currentArray = [...(this.filters[filterName] || [])]
      const index = currentArray.indexOf(value)
      
      if (index > -1) {
        currentArray.splice(index, 1)
      } else {
        currentArray.push(value)
      }
      
      this.updateFilter(filterName, currentArray)
    },
    
    clearAllFilters() {
      this.$emit('clear-filters')
    },

    formatLabel(value) {
      // Convert kebab-case or snake_case to Title Case
      return value
        .split(/[-_\s]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    
    getCategoryLabel(value) {
      return this.labelMappings[value] || this.formatLabel(value)
    }
  }
}
</script>

<style>
.text-theme {
  color: var(--theme);
}

.text-theme-hover {
  color: var(--theme-hover);
}

.bg-theme-light {
  background-color: var(--theme-light);
}

.bg-theme-bg {
  background-color: var(--theme);
}

.border-theme-border {
  border-color: var(--theme-border);
}

.focus\:ring-theme:focus {
  --tw-ring-color: var(--theme);
}
select {
  background: unset !important;
}
</style> 